import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { Collection } from "@shared/schemas/common";
import type {
  CompareResponse,
  ScenarioDetail,
  ScenarioVersionDetail,
  ScenarioVersionRow,
} from "@shared/schemas/scenario";
import { queryKeys } from "@client/lib/queryClient";
import {
  compareScenarios,
  getScenarioVersion,
  importScenario,
  listScenarioVersions,
  restoreScenarioVersion,
} from "@client/lib/scenariosApi";

/**
 * History, comparison and import as hooks — phase 8 of the plan.
 *
 * A version is immutable once written, so the two read hooks can sit on a long
 * `staleTime`: the list only grows, and a snapshot never changes at all. The
 * restore is the odd one out — it writes a *new* version — so it invalidates the
 * history alongside the scenario itself.
 */

/** A snapshot is immutable; only an hour later is it worth asking again. */
const IMMUTABLE = 60 * 60 * 1000;

export function useScenarioVersions(
  scenarioId: string | undefined,
): UseQueryResult<Collection<ScenarioVersionRow>> {
  return useQuery({
    queryKey: queryKeys.scenarios.versions(scenarioId ?? ""),
    queryFn: () => listScenarioVersions(scenarioId!),
    enabled: Boolean(scenarioId),
  });
}

export function useScenarioVersion(
  scenarioId: string | undefined,
  version: number | null,
): UseQueryResult<ScenarioVersionDetail> {
  return useQuery({
    queryKey: queryKeys.scenarios.version(scenarioId ?? "", version ?? 0),
    queryFn: () => getScenarioVersion(scenarioId!, version!),
    enabled: Boolean(scenarioId) && version !== null,
    staleTime: IMMUTABLE,
  });
}

export function useRestoreScenarioVersion(scenarioId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => restoreScenarioVersion(scenarioId, version),
    onSuccess: (response) => {
      // The response carries the restored scenario, so the editor's query is
      // written rather than refetched — it is looking at that exact key, and a
      // refetch would put a skeleton over content we are already holding.
      client.setQueryData(queryKeys.scenarios.detail(scenarioId), response.scenario);
      void client.invalidateQueries({ queryKey: queryKeys.scenarios.versions(scenarioId) });
      void client.invalidateQueries({ queryKey: queryKeys.scenarios.all });
      void client.invalidateQueries({ queryKey: queryKeys.products.all });
    },
  });
}

/**
 * The comparison. Disabled below two ids rather than erroring: `/compare` with
 * one scenario selected is a page in mid-edit, not a failure, and the screen
 * shows a picker instead.
 */
export function useCompareScenarios(ids: readonly string[]): UseQueryResult<CompareResponse> {
  return useQuery({
    queryKey: queryKeys.scenarios.compare(ids),
    queryFn: () => compareScenarios(ids),
    enabled: ids.length >= 2,
  });
}

export function useImportScenario(productId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (document: unknown) => importScenario(productId, document),
    onSuccess: (scenario: ScenarioDetail) => {
      client.setQueryData(queryKeys.scenarios.detail(scenario.id), scenario);
      void client.invalidateQueries({ queryKey: queryKeys.scenarios.all });
      void client.invalidateQueries({ queryKey: queryKeys.products.all });
    },
  });
}
